import {
	Container,
	Typography,
	TextField,
	MenuItem,
	Select,
	InputLabel,
	FormControl,
	Paper,
	Button,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Accordion,
	AccordionSummary,
	AccordionDetails
} from '@mui/material'
import Grid from '@mui/material/Grid2'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import { useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { useBatch } from '../hooks/useBatch'
import Switch from '@mui/material/Switch'
import { BatchReadOnlyData } from '../components/Batch/ReadOnlyData'
import { MovementDetailsModal } from '../components/Batch/MovementDetailsModal'
import useRole from '../hooks/useRole'

export const ManageBatch = () => {
	const { state } = useLocation()
	const { batchId } = state
	const { canWrite } = useRole()

	const { selectedBatch, fetchBatch, updateBatch, loadingItem, addHistory, getFile, addTest } =
		useBatch()

	const [editableFields, setEditableFields] = useState({
		rejection: '',
		available: '',
		losses: '',
		technician: ''
	})

	const [openModal, setOpenModal] = useState(false)
	const [selectedMovement, setSelectedMovement] = useState<any>(null)

	const [egreso, setEgreso] = useState({
		cantidad: '',
		unidad: '',
		fecha: '',
		motivo: '',
		source: 'available', // valor por defecto
		cliente: '',
		nit: '',
		factura: '',
		piloto: '',
		placa: '',
		beneficiario: '',
		documento: '',
		centro: '',
		guia: '',
		archivos: [] as File[],
		outcomeNumber: '',
		notas: '',
		programa: ''
	})

	const [newTest, setNewTest] = useState({
		testId: '',
		user: '',
		beginDate: '',
		endDate: '',
		result: '',
		attachments: [] as File[]
	})

	const [isEditable, setIsEditable] = useState(true)
	const [isMovementDisabled, setIsMovementDisabled] = useState(false)
	const [archiving, setArchiving] = useState(false)

	// Deshabilitar edición para usuarios AUDIT_ROLE
	const canEdit = canWrite()

	useEffect(() => {
		if (!canEdit) {
			setIsEditable(false)
			setIsMovementDisabled(true)
		}
	}, [canEdit])

	const handleChange = (field: string, value: string) => {
		setEditableFields({ ...editableFields, [field]: value })
	}

	const handleEgresoChange = (field: string, value: string) => {
		setEgreso({ ...egreso, [field]: value })
	}

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			setEgreso({ ...egreso, archivos: Array.from(event.target.files) })
		}
	}

	const handleTestChange = (field: string, value: string) => {
		setNewTest({ ...newTest, [field]: value })
	}

	const handleTestFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			setNewTest({ ...newTest, attachments: Array.from(event.target.files) })
		}
	}

	const handleAddTest = async () => {
		if (!newTest.beginDate || !newTest.endDate || !newTest.result) {
			alert('Por favor complete todos los campos para agregar una prueba.')
			return
		}

		const confirmationMessage =
			`¿Estás seguro de que deseas agregar esta prueba con la siguiente información?\n\n` +
			`Fecha de Inicio: ${newTest.beginDate}\n` +
			`Fecha Fin: ${newTest.endDate}\n` +
			`Resultado: ${newTest.result}`

		if (!window.confirm(confirmationMessage)) {
			return
		}

		const attachments = newTest.attachments
			? await Promise.all(
				newTest.attachments.map(
					(file) =>
						new Promise<{ ext: string; data: string; name: string }>((resolve) => {
							const reader = new FileReader()
							reader.onload = () => {
								const base64Data = reader.result?.toString().split(',')[1] || ''
								const ext = file.name.split('.').pop() || ''
								resolve({ ext, data: base64Data, name: file.name })
							}
							reader.readAsDataURL(file)
						})
				)
			)
			: []

		const payload = {
			testId: `test-${Date.now()}`, // Generate a unique test ID
			user: 'currentUser', // Replace with actual user data if available
			beginDate: newTest.beginDate,
			endDate: newTest.endDate,
			result: parseFloat(newTest.result),
			attachments
		}

		const result = await addTest(selectedBatch!.uid, payload)

		if (result) {
			alert('Prueba agregada correctamente.')
			setNewTest({ testId: '', user: '', beginDate: '', endDate: '', result: '', attachments: [] })
		} else {
			alert('Error al agregar la prueba.')
		}
	}

	useEffect(() => {
		fetchBatch(batchId)
	}, [batchId, fetchBatch])

	useEffect(() => {
		if (selectedBatch) {
			setEditableFields({
				rejection: selectedBatch.rejection.toString(),
				available: selectedBatch.available.toString(),
				losses: selectedBatch.losses.toString(),
				technician: selectedBatch.technician || ''
			})

			setIsEditable(
				selectedBatch.rejection === 0 && selectedBatch.available === 0 && selectedBatch.losses === 0
			)

			setIsMovementDisabled(
				selectedBatch.rejection === 0 && selectedBatch.available === 0 && selectedBatch.losses === 0
			)
		}
	}, [selectedBatch])

	const handleUpdate = async () => {
		const rejection = parseFloat(editableFields.rejection)
		const available = parseFloat(editableFields.available)
		const losses = parseFloat(editableFields.losses)

		if (rejection + available + losses > selectedBatch!.netweight) {
			alert('La suma de rechazo, semillas y pérdidas no puede ser mayor a la cantidad neta.')
			return
		}

		const confirmationMessage =
			`¿Estás seguro de que deseas actualizar los campos con la siguiente información?\n\n` +
			`Rechazo: ${rejection} Quintales\n` +
			`Semillas: ${available} Quintales\n` +
			`Pérdidas: ${losses} Quintales`

		if (!window.confirm(confirmationMessage)) {
			return
		}

		const updatedBatch = await updateBatch(batchId, {
			rejection,
			available,
			losses,
			rejectionBackup: rejection,
			availableBackup: available,
			lossesBackup: losses,
			technician: editableFields.technician
		})

		if (updatedBatch) {
			alert('Campos actualizados correctamente.')
		} else {
			alert('Error al actualizar los campos.')
		}
	}

	const handleRegisterEgreso = async () => {
		const confirmationMessage =
			`¿Estás seguro de que deseas registrar este egreso con la siguiente información?

` +
			`Cantidad: ${egreso.cantidad} Quintales
` +
			`Número de Salida: ${egreso.outcomeNumber}
` +
			`Fecha: ${egreso.fecha}
` +
			(egreso.motivo === 'sale'
				? `Cliente: ${egreso.cliente}, NIT: ${egreso.nit}, Factura: ${egreso.factura}
`
				: '') +
			(egreso.motivo === 'donation'
				? `Beneficiario: ${egreso.beneficiario}, Documento: ${egreso.documento}
`
				: '') +
			(egreso.motivo === 'transfer'
				? `Centro: ${egreso.centro}, Guía de Envío: ${egreso.guia}, Programa: ${egreso.programa}
`
				: '')

		if (!window.confirm(confirmationMessage)) {
			return
		}

		const attachments: Array<{ ext: string; data: string; name: string }> = await Promise.all(
			egreso.archivos.map(
				(file) =>
					new Promise<{ ext: string; data: string; name: string }>((resolve) => {
						const reader = new FileReader()
						reader.onload = () => {
							const base64Data = reader.result?.toString().split(',')[1] || ''
							const ext = file.name.split('.').pop() || ''
							resolve({ ext, data: base64Data, name: file.name })
						}
						reader.readAsDataURL(file)
					})
			)
		)

		const payload = {
			date: egreso.fecha,
			action: egreso.motivo as 'sale' | 'donation' | 'transfer',
			outcomeNumber: parseInt(egreso.outcomeNumber),
			attachments,
			mount: parseFloat(egreso.cantidad),
			notes: egreso.notas,
			source: egreso.source as 'rejection' | 'available',
			sale:
				egreso.motivo === 'sale'
					? {
						clientName: egreso.cliente,
						nit: egreso.nit,
						invoice: egreso.factura,
						driver: egreso.piloto,
						plateNumber: egreso.placa
					}
					: undefined,
			donation:
				egreso.motivo === 'donation'
					? {
						clientName: egreso.beneficiario,
						document: egreso.documento
					}
					: undefined,
			transfer:
				egreso.motivo === 'transfer'
					? {
						center: egreso.centro,
						shipment: egreso.guia,
						program: egreso.programa
					}
					: undefined
		}

		const result = await addHistory(selectedBatch!.uid, payload)

		if (result) {
			alert('Egreso registrado correctamente.')
			setEgreso({
				cantidad: '',
				unidad: '',
				fecha: '',
				motivo: '',
				cliente: '',
				nit: '',
				factura: '',
				piloto: '',
				placa: '',
				beneficiario: '',
				documento: '',
				centro: '',
				guia: '',
				archivos: [],
				outcomeNumber: '',
				notas: '',
				programa: '',
				source: 'available' // Reset to default
			})
		} else {
			alert('Error al registrar el egreso.')
		}
	}

	const handleDownloadFile = async (filePath: string) => {
		const fileData = await getFile(filePath)
		if (fileData) {
			const link = document.createElement('a')
			link.href = `data:application/octet-stream;base64,${fileData}`
			link.download = filePath.split('/').pop() || 'archivo'
			link.click()
		} else {
			alert('Error al descargar el archivo.')
		}
	}

	const handleRemoveEgresoFile = (index: number) => {
		setEgreso({
			...egreso,
			archivos: egreso.archivos.filter((_, i) => i !== index)
		})
	}

	const handleRemoveTestFile = (index: number) => {
		setNewTest({
			...newTest,
			attachments: newTest.attachments.filter((_, i) => i !== index)
		})
	}

	const handleToggleActive = async () => {
		if (!selectedBatch) return

		const updatedStatus = !selectedBatch.active

		const confirmationMessage = `¿Estás seguro de que deseas ${updatedStatus ? 'activar' : 'desactivar'} este lote?`

		if (!window.confirm(confirmationMessage)) {
			return
		}

		const result = await updateBatch(selectedBatch.uid, { active: updatedStatus })

		if (result) {
			alert(`Lote ${updatedStatus ? 'activado' : 'desactivado'} correctamente.`)
		} else {
			alert('Error al actualizar el estado del lote.')
		}
	}

	const getTipoMovimiento = (action: string) => {
		switch (action) {
			case 'sale':
				return 'Venta'
			case 'donation':
				return 'Donación'
			case 'transfer':
				return 'Traslado'
			case 'discard':
				return 'Descarte'
			default:
				return 'Desconocido'
		}
	}

	if (loadingItem) {
		return (
			<Container maxWidth="lg">
				<Typography variant="h4" gutterBottom textAlign="center" marginY={2}>
					Cargando información del lote...
				</Typography>
			</Container>
		)
	}

	if (!selectedBatch) {
		return (
			<Container maxWidth="lg">
				<Typography variant="h4" gutterBottom textAlign="center" marginY={2}>
					Lote no encontrado
				</Typography>
			</Container>
		)
	}

	return (
		<Container maxWidth="lg">
			<Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={2}>
				<Typography variant="h4" gutterBottom textAlign="center" marginY={2}>
					Lote # {selectedBatch.batchId}
				</Typography>
				<Box display="flex" alignItems="center">
					<Typography variant="body1" marginRight={2}>
						Estado del lote:
					</Typography>
					<Switch
						checked={selectedBatch?.active || false}
						onChange={handleToggleActive}
						color="primary"
					/>
				</Box>
			</Box>

			{/* Datos de solo lectura */}
			<BatchReadOnlyData selectedBatch={selectedBatch} />

			{/* Archivos Adjuntos del Batch */}
			{selectedBatch.attachments && selectedBatch.attachments.length > 0 && (
				<Box mt={2}>
					<Typography variant="h6">Archivos Adjuntos</Typography>
					<ul>
						{selectedBatch.attachments.map((attachment, index) => (
							<li key={index}>
								{attachment.split('/').pop()}
								<Button
									variant="text"
									startIcon={<DownloadIcon />}
									style={{ marginLeft: '8px' }}
									onClick={() => handleDownloadFile(attachment)}
								>
									Descargar
								</Button>
							</li>
						))}
					</ul>
				</Box>
			)}

			<hr style={{ marginTop: '4rem', marginBottom: '1rem' }} />

			{/* Campos adicionales editables: Rechazo, Semillas, Pérdidas */}

			<Typography variant="h6" marginBottom={2}>
				Disponibilidad
			</Typography>

			<Grid container spacing={2} mb={2}>
				<Grid size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Rechazo (Quintales)"
						type="number"
						fullWidth
						value={editableFields.rejection}
						onChange={(e) => handleChange('rejection', e.target.value)}
						disabled={!isEditable}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Semillas (Quintales)"
						type="number"
						fullWidth
						value={editableFields.available}
						onChange={(e) => handleChange('available', e.target.value)}
						disabled={!isEditable}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Pérdidas (Quintales)"
						type="number"
						fullWidth
						value={editableFields.losses}
						onChange={(e) => handleChange('losses', e.target.value)}
						disabled={!isEditable}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 4 }}>
					<TextField
						label="Nombre de Técnico"
						type="text"
						fullWidth
						value={editableFields.technician || ''}
						onChange={(e) => handleChange('technician', e.target.value)}
						disabled={!isEditable}
					/>
				</Grid>
			</Grid>

			{isEditable && (
				<Button
					variant="contained"
					color="primary"
					onClick={handleUpdate}
					style={{ marginTop: '1rem' }}
				>
					Actualizar Información
				</Button>
			)}

			<hr style={{ marginTop: '2rem', marginBottom: '2rem', border: '1px solid #fff' }} />

			{/* Sección Movimientos */}
			<Accordion defaultExpanded>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="h6">Movimientos</Typography>
				</AccordionSummary>
				<AccordionDetails>
					{/* Historial de Movimientos */}
					<Typography variant="subtitle1" marginY={2}>
						Historial de Movimientos
					</Typography>
					<TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Fecha</TableCell>
									<TableCell>Tipo</TableCell>
									<TableCell>Usuario</TableCell>
									<TableCell>Cantidad</TableCell>
									<TableCell>No. Egreso</TableCell>
									<TableCell>Acciones</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{selectedBatch.history && selectedBatch.history.length ? (
									selectedBatch.history.map((mov) => {
										const tipo = getTipoMovimiento(mov.action)
										return (
											<TableRow key={mov._id}>
												<TableCell>{new Date(mov.date).toLocaleDateString()}</TableCell>
												<TableCell>{tipo}</TableCell>
												<TableCell>{mov.user}</TableCell>
												<TableCell>{mov.mount}</TableCell>
												<TableCell>{mov.outcomeNumber}</TableCell>
												<TableCell>
													<Button
														variant="contained"
														onClick={() => {
															setSelectedMovement(mov)
															setOpenModal(true)
														}}
													>
														Ver Detalles
													</Button>
												</TableCell>
											</TableRow>
										)
									})
								) : (
									<TableRow>
										<TableCell colSpan={7}>No hay movimientos registrados</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					{/* Modal para mostrar los detalles de cada movimiento */}
					<MovementDetailsModal
						open={openModal}
						onClose={() => setOpenModal(false)}
						movement={selectedMovement}
						onDownloadFile={handleDownloadFile}
						getTipoMovimiento={getTipoMovimiento}
					/>

					{/* Sección de Dar Egreso de Semilla */}
					<Typography variant="subtitle1" marginY={2}>
						Dar Egreso de Semilla
					</Typography>
					<form
						onSubmit={(e) => {
							e.preventDefault()
							handleRegisterEgreso()
						}}
					>
						<Grid container spacing={2} mb={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
									label="Cantidad (Quintales)"
									type="number"
									fullWidth
									required
									value={egreso.cantidad}
									onChange={(e) => handleEgresoChange('cantidad', e.target.value)}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
									label="Número de Salida"
									type="number"
									fullWidth
									required
									value={egreso.outcomeNumber}
									onChange={(e) => handleEgresoChange('outcomeNumber', e.target.value)}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} mb={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<TextField
									type="date"
									label="Fecha"
									fullWidth
									required
									InputLabelProps={{ shrink: true }}
									value={egreso.fecha}
									onChange={(e) => handleEgresoChange('fecha', e.target.value)}
								/>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<FormControl fullWidth>
									<InputLabel>Motivo</InputLabel>
									<Select
										value={egreso.motivo}
										required
										onChange={(e) => handleEgresoChange('motivo', e.target.value)}
									>
										<MenuItem value="sale">Venta</MenuItem>
										<MenuItem value="donation">Donación</MenuItem>
										<MenuItem value="transfer">Traslado</MenuItem>
										<MenuItem value="discard">Descarte</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<FormControl fullWidth>
									<InputLabel>Origen</InputLabel>
									<Select
										value={egreso.source}
										required
										onChange={(e) => handleEgresoChange('source', e.target.value)}
									>
										<MenuItem value="rejection">Rechazo</MenuItem>
										<MenuItem value="available">Disponible</MenuItem>
									</Select>
								</FormControl>
							</Grid>
						</Grid>

						{/* Campos condicionales según motivo */}
						{egreso.motivo === 'sale' && (
							<Grid container spacing={2} mb={2}>
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										label="Cliente"
										fullWidth
										required
										value={egreso.cliente}
										onChange={(e) => handleEgresoChange('cliente', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										label="NIT"
										fullWidth
										required
										value={egreso.nit}
										onChange={(e) => handleEgresoChange('nit', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 4 }}>
									<TextField
										label="Factura"
										fullWidth
										required
										value={egreso.factura}
										onChange={(e) => handleEgresoChange('factura', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Piloto"
										fullWidth
										required
										value={egreso.piloto}
										onChange={(e) => handleEgresoChange('piloto', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Placa"
										fullWidth
										required
										value={egreso.placa}
										onChange={(e) => handleEgresoChange('placa', e.target.value)}
									/>
								</Grid>
							</Grid>
						)}
						{egreso.motivo === 'donation' && (
							<Grid container spacing={2} mb={2}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Beneficiario"
										fullWidth
										required
										value={egreso.beneficiario}
										onChange={(e) => handleEgresoChange('beneficiario', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Documento"
										fullWidth
										required
										value={egreso.documento}
										onChange={(e) => handleEgresoChange('documento', e.target.value)}
									/>
								</Grid>
							</Grid>
						)}
						{egreso.motivo === 'transfer' && (
							<Grid container spacing={2} mb={2}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Centro"
										fullWidth
										required
										value={egreso.centro}
										onChange={(e) => handleEgresoChange('centro', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Guía de Envío"
										fullWidth
										required
										value={egreso.guia}
										onChange={(e) => handleEgresoChange('guia', e.target.value)}
									/>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<TextField
										label="Programa"
										fullWidth
										required
										value={egreso.programa}
										onChange={(e) => handleEgresoChange('programa', e.target.value)}
									/>
								</Grid>
							</Grid>
						)}

						<Grid container spacing={2} mb={2}>
							<Grid size={{ xs: 12, sm: 12 }}>
								<TextField
									label="Notas"
									fullWidth
									multiline
									rows={4}
									value={egreso.notas}
									onChange={(e) => handleEgresoChange('notas', e.target.value)}
								/>
							</Grid>
						</Grid>
						<Grid container spacing={2} mb={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Button variant="outlined" startIcon={<AttachFileIcon />} component="label">
									Archivos Adjuntos
									<input type="file" multiple hidden onChange={handleFileChange} />
								</Button>
								<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
									Tamaño máximo por archivo: 100MB
								</Typography>
								<ul>
									{egreso.archivos.map((file, index) => (
										<li key={index}>										{file.name}
										{canEdit && (
											<Button
												variant="text"
												color="secondary"
												onClick={() => handleRemoveEgresoFile(index)}
												style={{ marginLeft: '8px' }}
											>
												<DeleteIcon style={{ color: 'gray' }} />
											</Button>
										)}
										</li>
									))}
								</ul>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Button type="submit" variant="contained" fullWidth disabled={isMovementDisabled || !canEdit}>
									Registrar Egreso
								</Button>
							</Grid>
						</Grid>
					</form>
				</AccordionDetails>
			</Accordion>

			<hr style={{ marginTop: '2rem', marginBottom: '2rem', border: '1px solid #fff' }} />

			<Accordion>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="h6">Pruebas de Germinación</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Typography variant="subtitle1" marginY={2}>
						Historial de Pruebas
					</Typography>
					<TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
						<Table stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Fecha de Inicio</TableCell>
									<TableCell>Fecha Fin</TableCell>
									<TableCell>Resultado</TableCell>
									<TableCell>Usuario</TableCell>
									<TableCell>Archivos</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{selectedBatch.test && selectedBatch.test.length ? (
									selectedBatch.test.map((test) => (
										<TableRow key={test.testId}>
											<TableCell>{new Date(test.beginDate).toLocaleDateString()}</TableCell>
											<TableCell>{new Date(test.endDate).toLocaleDateString()}</TableCell>
											<TableCell>{test.result}</TableCell>
											<TableCell>{test.user}</TableCell>
											<TableCell>
												{test.attachments && test.attachments.length > 0 ? (
													<ul>
														{test.attachments.map((attachment, index) => (
															<li key={index}>
																{attachment.split('/').pop()}
																<Button
																	variant="text"
																	startIcon={<DownloadIcon />}
																	style={{ marginLeft: '8px' }}
																	onClick={() => handleDownloadFile(attachment)}
																>
																	Descargar
																</Button>
															</li>
														))}
													</ul>
												) : (
													'No hay archivos adjuntos'
												)}
											</TableCell>
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell colSpan={5}>No hay pruebas registradas</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<Typography variant="subtitle1" marginY={2}>
						Agregar Prueba
					</Typography>
					<Grid container spacing={2} mb={2}>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								type="date"
								label="Fecha de Inicio"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={newTest.beginDate}
								onChange={(e) => handleTestChange('beginDate', e.target.value)}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								type="date"
								label="Fecha Fin"
								fullWidth
								InputLabelProps={{ shrink: true }}
								value={newTest.endDate}
								onChange={(e) => handleTestChange('endDate', e.target.value)}
							/>
						</Grid>
					</Grid>
					<Grid container spacing={2} mb={2}>
						<Grid size={{ xs: 12, sm: 6 }}>
							<TextField
								label="Resultado"
								fullWidth
								value={newTest.result}
								onChange={(e) => handleTestChange('result', e.target.value)}
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 6 }}>
							<Button variant="outlined" startIcon={<AttachFileIcon />} component="label">
								Archivos Adjuntos
								<input type="file" multiple hidden onChange={handleTestFileChange} />
							</Button>
							<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
								Tamaño máximo por archivo: 100MB
							</Typography>
							<ul>
								{newTest.attachments &&
									newTest.attachments.map((file, index) => (
										<li key={index}>
											{file.name}
											<Button
												variant="text"
												color="secondary"
												onClick={() => handleRemoveTestFile(index)}
												style={{ marginLeft: '8px' }}
											>
												<DeleteIcon style={{ color: 'gray' }} />
											</Button>
										</li>
									))}
							</ul>
						</Grid>
					</Grid>
					<Grid container spacing={2} mb={2}>
						<Grid size={{ xs: 12, sm: 6 }}>
							<Button 
								variant="contained" 
								color="primary" 
								fullWidth 
								onClick={handleAddTest}
								disabled={!canEdit}
							>
								Agregar Prueba
							</Button>
						</Grid>
					</Grid>
				</AccordionDetails>
			</Accordion>

			<hr style={{ marginTop: '2rem', marginBottom: '4rem', border: '1px solid #fff' }} />
		</Container>
	)
}
